import React, { useState, useEffect } from 'react';
import './RemindersModal.css';
import { getReminderTime, setReminderTime, rescheduleNotification, checkNotificationPermission, hasPlayedToday, isRemindersEnabled, setRemindersEnabled, hasAskedForPermission, requestNotificationPermission } from '../utils/notificationService';
import { isTutorialGame } from '../utils/tutorialManager';
import { isNative } from '../utils/platform';

function RemindersModal({ onClose, langString, game }) {
  const [hour, setHour] = useState(18);
  const [minute, setMinute] = useState(0);
  const [hasPermission, setHasPermission] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [enabled, setEnabled] = useState(true);

  // Use 12-hour format (AM/PM) only for English, 24-hour format for Dutch
  const uses12HourFormat = langString === 'en';

  // Generate hour options based on format
  const [amPm, setAmPm] = useState('PM'); // Default, will be updated when hour loads

  useEffect(() => {
    // Load current reminder time
    const time = getReminderTime();
    setHour(time.hour);
    setMinute(time.minute);
    // Update AM/PM based on loaded hour
    setAmPm(time.hour >= 12 ? 'PM' : 'AM');

    // Load enabled state
    setEnabled(isRemindersEnabled());

    // Check permission status and request if not asked before
    const checkAndRequestPermission = async () => {
      const permission = await checkNotificationPermission();
      
      // Only ask for permission if it's a regular daily game (not tutorial games 9999, 9998, 9997)
      const isRegularGame = game && !isTutorialGame(game.idNumber) && !game.creator;
      
      // If permission not granted and never asked before, ask now
      if (!permission.granted && !hasAskedForPermission() && isRegularGame) {
        console.log('[RemindersModal] Permission never asked before, requesting now...');
        const result = await requestNotificationPermission();
        setHasPermission(result.granted);
        
        // If permission granted, schedule notification for tomorrow at current time + 1 hour
        if (result.granted) {
          import('../utils/notificationService').then(({ scheduleNotificationForTomorrow }) => {
            scheduleNotificationForTomorrow(undefined, true).catch((error) => {
              console.warn('Failed to schedule notification after granting permission:', error);
            });
          });
        }
      } else {
        setHasPermission(permission.granted);
      }
    };

    checkAndRequestPermission();
  }, [game]);

  const handleToggle = () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    setRemindersEnabled(newEnabled);
    
    if (!newEnabled) {
      // If disabling, cancel today's notification
      import('../utils/notificationService').then(({ cancelTodaysNotification }) => {
        cancelTodaysNotification();
      });
    } else if (hasPermission && !hasPlayedToday()) {
      // If enabling and has permission, schedule notification
      rescheduleNotification(hour, minute).catch((error) => {
        console.warn('Failed to schedule notification after enabling:', error);
      });
    }
  };

  const handleSave = async () => {
    if (!enabled) {
      onClose();
      return;
    }

    setIsSaving(true);
    setSaveMessage('');

    try {
      // ALWAYS reschedule notification with selected time, regardless of whether user played today
      const result = await rescheduleNotification(hour, minute);
      if (result.scheduled) {
        const hasPlayed = hasPlayedToday();
        setSaveMessage(langString === 'nl' 
          ? (hasPlayed 
              ? 'Herinneringstijd opgeslagen! De nieuwe tijd wordt vanaf morgen gebruikt.'
              : 'Herinneringstijd opgeslagen!')
          : (hasPlayed
              ? 'Reminder time saved! The new time will be used from tomorrow.'
              : 'Reminder time saved!'));
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setSaveMessage(langString === 'nl'
          ? 'Kon herinnering niet plannen. Controleer je meldingen-instellingen.'
          : 'Could not schedule reminder. Please check your notification settings.');
      }
    } catch (error) {
      console.error('Failed to save reminder:', error);
      setSaveMessage(langString === 'nl'
        ? 'Er ging iets mis bij het opslaan.'
        : 'Something went wrong while saving.');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  // Update AM/PM when hour changes (but not when loading initial time)
  useEffect(() => {
    // Only update if hour is already set (not initial load)
    if (hour !== undefined) {
      setAmPm(hour >= 12 ? 'PM' : 'AM');
    }
  }, [hour]);

  // Convert 24-hour to 12-hour for display
  const getDisplayHour = (h24) => {
    if (h24 === 0) return 12;
    if (h24 > 12) return h24 - 12;
    return h24;
  };

  // Convert 12-hour + AM/PM to 24-hour
  const convertTo24Hour = (h12, amPmValue) => {
    if (amPmValue === 'AM') {
      if (h12 === 12) return 0;
      return h12;
    } else { // PM
      if (h12 === 12) return 12;
      return h12 + 12;
    }
  };

  // Handle hour change in 12-hour format
  const handle12HourChange = (newDisplayHour, newAmPm) => {
    const newHour24 = convertTo24Hour(newDisplayHour, newAmPm);
    setHour(newHour24);
    setAmPm(newAmPm);
  };

  // Generate hour options based on format
  const hourOptions = uses12HourFormat
    ? Array.from({ length: 12 }, (_, i) => i + 1) // 1-12 for 12-hour format
    : Array.from({ length: 24 }, (_, i) => i); // 0-23 for 24-hour format
  
  // Generate minute options (0, 15, 30, 45)
  const minuteOptions = [0, 15, 30, 45];

  const explanationText = langString === 'nl'
    ? 'Je krijgt elke dag een herinnering om WordHit te spelen, maar alleen als je die dag nog niet hebt gespeeld. Zodra je speelt, wordt de herinnering voor die dag geannuleerd.'
    : 'You will receive a daily reminder to play WordHit, but only if you haven\'t played yet that day. Once you play, the reminder for that day is cancelled.';

  const handleOpenSettings = async () => {
    if (!isNative()) {
      return;
    }
    try {
      const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
      const result = await NativeSettings.open({
        optionAndroid: AndroidSettings.AppNotification,
        optionIOS: IOSSettings.App,
      });
      if (!result.status) {
        console.warn('Failed to open app settings:', result);
      }
    } catch (error) {
      console.warn('Failed to open app settings:', error);
    }
  };

  return (
    <>
      <div className="reminders-overlay" onClick={(e) => {
        if (e.target === e.currentTarget) {
          e.stopPropagation();
          onClose();
        }
      }} />
      <div className="reminders-modal">
        <button
          className="reminders-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="reminders-title">
          {langString === 'nl' ? 'HERINNERINGEN' : 'REMINDERS'}
        </h2>

        <div className="reminders-body">
          <p className="reminders-explanation">{explanationText}</p>

          {!hasPermission && (
            <p className="reminders-warning">
              {langString === 'nl' ? (
                <>Herinneringen zijn niet ingeschakeld. Ga naar <button type="button" className="reminders-settings-link" onClick={handleOpenSettings}>Instellingen</button> om herinneringen in te schakelen.</>
              ) : (
                <>Notifications are not enabled. Go to <button type="button" className="reminders-settings-link" onClick={handleOpenSettings}>Settings</button> to enable notifications.</>
              )}
            </p>
          )}

          <div className="reminders-toggle-section">
            <label className="reminders-toggle-label">
              {langString === 'nl' ? 'Herinneringen inschakelen' : 'Enable reminders'}
            </label>
            <button
              className={`reminders-toggle ${enabled ? 'reminders-toggle--on' : 'reminders-toggle--off'}`}
              onClick={handleToggle}
              disabled={!hasPermission}
            >
              <span className="reminders-toggle-slider" />
            </button>
          </div>

          {enabled && (
            <div className="reminders-time-selector">
              <label className="reminders-label">
                {langString === 'nl' ? 'Herinneringstijd:' : 'Reminder time:'}
              </label>
              
              <div className="reminders-time-inputs">
                {uses12HourFormat ? (
                  <>
                    <select
                      className="reminders-select"
                      value={getDisplayHour(hour)}
                      onChange={(e) => {
                        const newDisplayHour = parseInt(e.target.value);
                        handle12HourChange(newDisplayHour, amPm);
                      }}
                      disabled={isSaving || !hasPermission}
                    >
                      {hourOptions.map((h) => (
                        <option key={h} value={h}>
                          {String(h)}
                        </option>
                      ))}
                    </select>
                    
                    <span className="reminders-separator">:</span>
                    
                    <select
                      className="reminders-select"
                      value={minute}
                      onChange={(e) => setMinute(parseInt(e.target.value))}
                      disabled={isSaving || !hasPermission}
                    >
                      {minuteOptions.map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, '0')}
                        </option>
                      ))}
                    </select>

                    <select
                      className="reminders-select reminders-am-pm"
                      value={amPm}
                      onChange={(e) => {
                        const newAmPm = e.target.value;
                        handle12HourChange(getDisplayHour(hour), newAmPm);
                      }}
                      disabled={isSaving || !hasPermission}
                    >
                      <option value="AM">AM</option>
                      <option value="PM">PM</option>
                    </select>
                  </>
                ) : (
                  <>
                    <select
                      className="reminders-select"
                      value={hour}
                      onChange={(e) => setHour(parseInt(e.target.value))}
                      disabled={isSaving || !hasPermission}
                    >
                      {hourOptions.map((h) => (
                        <option key={h} value={h}>
                          {String(h).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    
                    <span className="reminders-separator">:</span>
                    
                    <select
                      className="reminders-select"
                      value={minute}
                      onChange={(e) => setMinute(parseInt(e.target.value))}
                      disabled={isSaving || !hasPermission}
                    >
                      {minuteOptions.map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </>
                )}
              </div>
            </div>
          )}

          {saveMessage && (
            <div className={`reminders-message ${saveMessage.includes('error') || saveMessage.includes('mis') ? 'reminders-message--error' : 'reminders-message--success'}`}>
              {saveMessage}
            </div>
          )}
        </div>

        <div className="reminders-footer">
          <button
            className="reminders-save-button"
            onClick={handleSave}
            disabled={isSaving || !enabled}
          >
            {isSaving
              ? (langString === 'nl' ? 'Opslaan...' : 'Saving...')
              : (langString === 'nl' ? 'Opslaan' : 'Save')}
          </button>
        </div>
      </div>
    </>
  );
}

export default RemindersModal;

